/**
 * AdvancedRackCanvas — rack grid orchestrator.
 *
 * Responsibilities (SRP):
 * - DnD context setup
 * - Slot/grid layout computation
 * - Rendering sub-components from canvas/ directory
 * - Tooltip mouse tracking
 *
 * NOT responsible for: constants, per-device rendering logic, type definitions.
 * Those live in canvas/equipmentTypes.ts, canvas/GridEquipment.tsx, etc.
 */
import React, { useState, useRef, useCallback, useMemo } from 'react';
import {
  DndContext,
  DragEndEvent,
  useSensor,
  useSensors,
  PointerSensor,
  closestCenter,
} from '@dnd-kit/core';
import { ProjectionMode } from './RackCanvasToolbar';

// Canvas sub-components
import { GridEquipment }                          from '../canvas/GridEquipment';
import { GridGhostEquipment }                     from '../canvas/GridGhostEquipment';
import { GridReservation }                        from '../canvas/GridReservation';
import { ZeroUEquipment, ZeroUDroppableColumn, ZeroUGhostPocket } from '../canvas/ZeroUEquipment';
import { PduOutletMatrixPopover }                  from '../canvas/PduOutletMatrixPopover';
import { DroppableSlot }                          from '../canvas/DroppableSlot';
import { DeviceTooltip }                          from '../canvas/DeviceTooltip';

// Re-export types and constants so existing importers don't break
export type { RackEquipmentItem, CableConnection } from '../canvas/rackTypes';
export { TYPE_COLORS, TYPE_NAMES_RU }              from '../canvas/equipmentTypes';

import { toast }                                    from 'react-hot-toast';
import type { RackEquipmentItem, CableConnection } from '../canvas/rackTypes';
import type { RackReservation }                    from '../../utils/rackReservation.logic';
import { getReservationUnitRange }                 from '../../utils/rackReservation.logic';
import { getGhostDevices }                         from '../../utils/halfDepth.logic';
import { resolveEquipmentTenant }                  from '../../utils/tenancyCascade.logic';
import { getZeroUSideColumns, canMountInZeroU }     from '../../utils/rackZeroU.logic';

interface AdvancedRackCanvasProps {
  uSlots: number;
  equipmentList: RackEquipmentItem[];
  cablesList?: CableConnection[];
  projectionMode: ProjectionMode;
  zoomLevel?: number;
  activeFilter: string;
  activeMode: 'inspect' | 'thermal' | 'cable';
  isMountingMode?: boolean;
  onEquipmentMove: (equipmentId: string, newU: number, newMount: string) => void;
  onSlotClick?: (u: number, mount: string) => void;
  onEquipmentClick?: (eq: RackEquipmentItem) => void;
  cablingDraft?: { sourceEqId: string; sourcePortName: string; sourcePortId: string; x: number; y: number } | null;
  onCablingDraftConnect?: (destEqId: string) => void;
  /** Current app theme — canvas adapts its palette accordingly */
  theme?: 'dark' | 'light';
  /** Show/hide the empty slot droppable cells (setting from AdminSettings) */
  showEmptySlots?: boolean;
  /** Live telemetry metrics for devices */
  liveMetrics?: Record<string, {temp_c?: number, power_w?: number}>;
  /** PDU outlet summaries keyed by equipment id */
  pduSummaries?: Record<string, { free_outlets: number; total_outlets: number }>;
  /** NetBox slot reservations */
  reservations?: RackReservation[];
  onReservationClick?: (res: RackReservation) => void;
  onReleaseReservation?: (res: RackReservation) => void;
  /** Tenant assigned to the parent rack cabinet */
  rackTenant?: string;
  /** Active tenant filter: dims equipment from other tenants */
  activeTenantFilter?: string;
}

export const AdvancedRackCanvas: React.FC<AdvancedRackCanvasProps> = ({
  uSlots,
  equipmentList,
  projectionMode,
  zoomLevel: _zoomLevel,
  activeFilter,
  activeMode,
  isMountingMode = false,
  onEquipmentMove,
  onSlotClick,
  onEquipmentClick,
  cablingDraft,
  onCablingDraftConnect,
  theme = 'dark',
  showEmptySlots: _showEmptySlots = true,
  liveMetrics = {},
  pduSummaries = {},
  reservations = [],
  onReservationClick,
  onReleaseReservation,
  rackTenant,
  activeTenantFilter = 'all',
}) => {
  const isDark = theme === 'dark';
  const [hoveredEqId, setHoveredEqId]       = useState<string | null>(null);
  const [activeDragItem, setActiveDragItem] = useState<RackEquipmentItem | null>(null);
  const [activeOverSlot, setActiveOverSlot] = useState<{ u: number; mount: string } | null>(null);
  const [tooltipData, setTooltipData]       = useState<{ eq: RackEquipmentItem; clientX: number; clientY: number } | null>(null);
  const [pduMatrixEq, setPduMatrixEq]       = useState<RackEquipmentItem | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const slotHeight = 24; // Fixed 24px per U — readable at any rack size

  const sideColumns = useMemo(() => {
    return getZeroUSideColumns(equipmentList);
  }, [equipmentList]);

  const gridEquipment = equipmentList.filter(eq => {
    if (eq.is_zero_u) return false;
    const mount = eq.mounting_position?.toLowerCase() || '';
    if (projectionMode === 'front') return mount === 'front' || mount === 'full depth';
    if (projectionMode === 'rear')  return mount === 'rear'  || mount === 'full depth';
    return true;
  });

  const ghostItems = useMemo(() => {
    return getGhostDevices(equipmentList, projectionMode);
  }, [equipmentList, projectionMode]);

  const slots = Array.from({ length: uSlots }, (_, i) => uSlots - i);

  const checkIsSlotValid = useCallback((targetU: number, targetMount: string) => {
    if (!activeDragItem) return true;
    const reqH = activeDragItem.u_height || 1;
    if (targetU + reqH - 1 > uSlots) return false;
    for (const other of equipmentList) {
      if (other.id === activeDragItem.id || other.is_zero_u) continue;
      const otherU     = Number(other.u_position);
      const otherH     = Number(other.u_height);
      const otherMount = other.mounting_position?.toLowerCase() || '';
      const isMountOverlap =
        targetMount.toLowerCase() === 'full depth' ||
        otherMount === 'full depth' ||
        targetMount.toLowerCase() === otherMount;
      if (isMountOverlap) {
        const isUOverlap = (targetU <= otherU + otherH - 1) && (targetU + reqH - 1 >= otherU);
        if (isUOverlap) return false;
      }
    }

    // Reservation collision check
    if (reservations && reservations.length > 0) {
      for (const res of reservations) {
        const { start, end } = getReservationUnitRange(res);
        const isUOverlap = (targetU <= end) && (targetU + reqH - 1 >= start);
        if (isUOverlap) {
          const resFace = (res.face || 'both').toLowerCase();
          const targetMountLower = targetMount.toLowerCase();
          const isFull = targetMountLower === 'full depth' || resFace === 'both';
          const isFaceConflict = isFull || targetMountLower === resFace;
          if (isFaceConflict) return false;
        }
      }
    }

    return true;
  }, [activeDragItem, equipmentList, reservations, uSlots]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;
    const eqId = active.id as string;
    const targetData = over.data.current;
    if (targetData?.isZeroU) {
      if (activeDragItem && !canMountInZeroU(activeDragItem)) {
        toast.error(
          `Оборудование "${activeDragItem.name}" (${activeDragItem.u_height || 1}U) нельзя смонтировать в 0U канал`
        );
        return;
      }
      onEquipmentMove(eqId, 0, targetData.mount);
      return;
    }
    if (targetData?.u !== undefined && targetData?.mount !== undefined) {
      if (checkIsSlotValid(targetData.u, targetData.mount)) {
        onEquipmentMove(eqId, targetData.u, targetData.mount);
      }
    }
  };

  const handleMousePos = useCallback((eq: RackEquipmentItem, clientX: number, clientY: number) => {
    setTooltipData({ eq, clientX, clientY });
  }, []);

  const handleMouseLeave = useCallback(() => {
    setTooltipData(null);
    setHoveredEqId(null);
  }, []);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={({ active }) => setActiveDragItem(active.data.current?.equipment)}
      onDragOver={({ over }) => setActiveOverSlot(
        over ? { u: over.data.current?.u, mount: over.data.current?.mount } : null
      )}
      onDragEnd={(event) => {
        setActiveDragItem(null);
        setActiveOverSlot(null);
        handleDragEnd(event);
      }}
    >
      {/* Canvas Container */}
      <div
        ref={containerRef}
        className="flex gap-1.5 p-1.5 w-full rounded-xl shadow-2xl relative select-none border border-[var(--border-color)] bg-[var(--bg-deep)]"
        onMouseLeave={handleMouseLeave}
      >
        {/* Left Zero-U / PDU panel */}
        <div
          data-testid="zero-u-left"
          className="w-14 shrink-0 border border-[var(--border-color)] bg-[var(--bg-panel)] rounded-lg flex flex-col relative overflow-hidden transition-colors"
        >
          <div className="text-center text-[8px] font-mono tracking-wider font-bold py-1 border-b border-[var(--border-color)] bg-[var(--bg-deep)] text-[var(--text-muted)] flex items-center justify-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 shrink-0 shadow-[0_0_6px_rgba(6,182,212,0.6)]" aria-hidden />
            <span>PDU A</span>
          </div>
          <ZeroUDroppableColumn mount="Left" onClick={() => onSlotClick?.(0, 'Left')}>
            {!sideColumns.left.isGhost ? (
              <>
                {sideColumns.left.equipments.map(eq => (
                  <ZeroUEquipment
                    key={eq.id}
                    eq={eq}
                    isDark={isDark}
                    isMountingMode={isMountingMode}
                    onClick={() => onEquipmentClick?.(eq)}
                    pduSummary={eq.equipment_type?.toLowerCase() === 'pdu' ? (pduSummaries[eq.id] ?? null) : null}
                    onOpenOutletMatrix={() => setPduMatrixEq(eq)}
                  />
                ))}
                {isMountingMode && (
                  <button
                    type="button"
                    onClick={() => onSlotClick?.(0, 'Left')}
                    className="w-full py-2 rounded-xs border border-dashed border-cyan-500/50 hover:border-cyan-400 bg-cyan-950/20 hover:bg-cyan-950/40 text-center text-[8px] font-mono text-cyan-300 transition-all cursor-pointer flex flex-col items-center justify-center gap-0.5"
                    title="Добавить дополнительное 0U оборудование (Ввод A)"
                  >
                    <span className="text-xs font-bold leading-none">+</span>
                    <span>0U (A)</span>
                  </button>
                )}
              </>
            ) : (
              <ZeroUGhostPocket
                mount="Left"
                label="+ PDU A (0U)"
                isMountingMode={isMountingMode}
                onClick={() => onSlotClick?.(0, 'Left')}
              />
            )}
          </ZeroUDroppableColumn>
        </div>

        {/* ─── Main Rack Chassis ─── */}
        <div
          data-testid="rack-main-grid"
          className={`flex-1 min-w-0 rounded-xl flex flex-col relative px-2 py-2 transition-all duration-300 ${
            isDark
              ? 'border-2 border-slate-600 bg-slate-950 shadow-[0_0_40px_rgba(0,0,0,0.9),inset_0_0_0_1px_rgba(100,116,139,0.1)]'
              : 'border-2 border-gray-400 bg-white shadow-[0_4px_20px_rgba(0,0,0,0.15),inset_0_0_0_1px_rgba(0,0,0,0.05)]'
          }`}
        >
          {/* Left/Right vertical rails removed - they were crossing the U-ruler */}
          {/* Front / Rear header (Uses exact same grid columns as the rack to guarantee alignment) */}
          <div 
            className="w-full grid text-[9px] font-mono tracking-widest uppercase font-bold text-center"
            style={{ gridTemplateColumns: `28px ${projectionMode === 'split' ? 'minmax(0, 1fr) 2px minmax(0, 1fr)' : 'minmax(0, 1fr)'}` }}
          >
            <div className={`border-b-2 border-r ${isDark ? 'border-slate-700 bg-slate-900/80' : 'border-gray-300 bg-gray-50'}`} />
            
            {projectionMode === 'split' ? (
              <>
                <div className={`flex items-center justify-center gap-1 py-1.5 border-b-2 ${
                  isDark ? 'border-slate-700 bg-slate-900/80 text-cyan-400/80' : 'border-gray-300 bg-gray-50 text-blue-600'
                }`}>
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-current opacity-60" />
                  Фронт
                </div>
                {/* Header Split Line */}
                <div className={isDark ? 'bg-slate-600' : 'bg-gray-400'} />
                <div className={`flex items-center justify-center gap-1 py-1.5 border-b-2 ${
                  isDark ? 'border-slate-700 bg-slate-900/80 text-indigo-400/80' : 'border-gray-300 bg-gray-50 text-indigo-600'
                }`}>
                  Тыл
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-current opacity-60" />
                </div>
              </>
            ) : projectionMode === 'rear' ? (
              <div className={`py-1.5 border-b-2 flex justify-center items-center ${isDark ? 'border-slate-700 bg-slate-900/80 text-slate-300' : 'border-gray-300 bg-gray-50 text-gray-600'}`}>Тыльная сторона</div>
            ) : projectionMode === 'isometric' ? (
              <div className={`py-1.5 border-b-2 flex justify-center items-center ${isDark ? 'border-slate-700 bg-slate-900/80 text-slate-300' : 'border-gray-300 bg-gray-50 text-gray-600'}`}>Изометрический вид</div>
            ) : (
              <div className={`py-1.5 border-b-2 flex justify-center items-center ${isDark ? 'border-slate-700 bg-slate-900/80 text-slate-300' : 'border-gray-300 bg-gray-50 text-gray-600'}`}>Лицевая панель (19″)</div>
            )}
          </div>

          {/* U-number ruler + equipment grid COMBINED */}
          <div
            className="flex-1 min-w-0 relative grid"
            style={{
              gridTemplateRows:    `repeat(${uSlots}, ${slotHeight}px)`,
              gridTemplateColumns: `28px ${projectionMode === 'split' ? 'minmax(0, 1fr) 2px minmax(0, 1fr)' : 'minmax(0, 1fr)'}`,
              minHeight:           `${uSlots * slotHeight}px`,
            }}
          >
            {/* Split vertical divider as a real grid column */}
            {projectionMode === 'split' && (
              <div
                className={`z-10 ${isDark ? 'bg-slate-500' : 'bg-gray-400'}`}
                style={{ gridColumn: 3, gridRow: '1 / -1' }}
              />
            )}

            {/* U-ruler (Column 1) */}
            {slots.map(u => (
              <div
                key={`u-${u}`}
                className={`w-full h-full flex items-center justify-center text-center leading-none text-[9px] font-mono font-bold border-b border-r ${
                  isDark
                    ? 'text-slate-500 border-slate-700/80'
                    : 'text-gray-400 border-gray-300'
                }`}
                style={{ gridRow: `${uSlots - u + 1}`, gridColumn: 1 }}
              >
                {u}
              </div>
            ))}
              {/* Empty droppable slots */}
              {slots.map(u => (
                projectionMode === 'split' ? (
                  ['Front', 'Rear'].map(mount => (
                    <DroppableSlot
                      key={`${u}-${mount}`}
                      u={u}
                      mount={mount}
                      isDark={isDark}
                      onClick={() => isMountingMode && onSlotClick?.(u, mount)}
                      style={{ gridRow: `${uSlots - u + 1}`, gridColumn: mount === 'Front' ? 2 : 4 }}
                      activeOverSlot={activeOverSlot}
                      isValidTarget={checkIsSlotValid(u, mount)}
                    />
                  ))
                ) : (
                  <DroppableSlot
                    key={u}
                    u={u}
                    mount={projectionMode === 'rear' ? 'Rear' : 'Front'}
                    isDark={isDark}
                    onClick={() => isMountingMode && onSlotClick?.(u, projectionMode === 'rear' ? 'Rear' : 'Front')}
                    style={{ gridRow: `${uSlots - u + 1}`, gridColumn: 2 }}
                    activeOverSlot={activeOverSlot}
                    isValidTarget={checkIsSlotValid(u, projectionMode === 'rear' ? 'Rear' : 'Front')}
                  />
                )
              ))}

              {/* Standalone Ghost equipment (rear on front view, front on rear view) */}
              {ghostItems.filter(g => !g.isCoexisting).map(ghost => (
                <GridGhostEquipment
                  key={`ghost-${ghost.eq.id}`}
                  ghost={ghost}
                  uSlots={uSlots}
                  isDark={isDark}
                  isMountingMode={isMountingMode}
                  onSlotClick={onSlotClick}
                />
              ))}

              {/* Mounted equipment */}
              {gridEquipment.map(eq => {
                const coexistingGhost = ghostItems.find(g => g.isCoexisting && g.coexistingWith?.id === eq.id);
                const isTypeDimmed = !!activeFilter && activeFilter !== 'all' && eq.equipment_type !== activeFilter;
                const isTenantDimmed = !!activeTenantFilter && activeTenantFilter !== 'all' &&
                  resolveEquipmentTenant(eq.tenant || (eq.attributes as any)?.tenant, rackTenant).effectiveTenant.toLowerCase() !== activeTenantFilter.toLowerCase();
                return (
                  <GridEquipment
                    key={eq.id}
                    eq={eq}
                    uSlots={uSlots}
                    activeMode={activeMode}
                    isMountingMode={isMountingMode}
                    projectionMode={projectionMode}
                    isDimmed={isTypeDimmed || isTenantDimmed}
                    hoveredEqId={hoveredEqId}
                    setHoveredEqId={setHoveredEqId}
                    onClick={onEquipmentClick}
                    cablingDraft={cablingDraft}
                    onCablingDraftConnect={onCablingDraftConnect}
                    onMousePos={handleMousePos}
                    isDark={isDark}
                    liveMetrics={liveMetrics[eq.id]}
                    pduSummary={eq.equipment_type?.toLowerCase() === 'pdu' ? (pduSummaries[eq.id] ?? null) : null}
                    coexistingOpposite={coexistingGhost ? coexistingGhost.eq : null}
                    rackTenant={rackTenant}
                  />
                );
              })}

              {/* NetBox Slot Reservations */}
              {reservations.map(res => (
                <GridReservation
                  key={res.id}
                  res={res}
                  uSlots={uSlots}
                  projectionMode={projectionMode}
                  isDark={isDark}
                  onClick={onReservationClick}
                  onRelease={onReleaseReservation}
                />
              ))}
            </div>
        </div>

        {/* Right Zero-U / PDU panel */}
        <div
          data-testid="zero-u-right"
          className="w-14 shrink-0 border border-[var(--border-color)] bg-[var(--bg-panel)] rounded-lg flex flex-col relative overflow-hidden transition-colors"
        >
          <div className="text-center text-[8px] font-mono tracking-wider font-bold py-1 border-b border-[var(--border-color)] bg-[var(--bg-deep)] text-[var(--text-muted)] flex items-center justify-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 shrink-0 shadow-[0_0_6px_rgba(129,140,248,0.6)]" aria-hidden />
            <span>PDU B</span>
          </div>
          <ZeroUDroppableColumn mount="Right" onClick={() => onSlotClick?.(0, 'Right')}>
            {!sideColumns.right.isGhost ? (
              <>
                {sideColumns.right.equipments.map(eq => (
                  <ZeroUEquipment
                    key={eq.id}
                    eq={eq}
                    isDark={isDark}
                    isMountingMode={isMountingMode}
                    onClick={() => onEquipmentClick?.(eq)}
                    pduSummary={eq.equipment_type?.toLowerCase() === 'pdu' ? (pduSummaries[eq.id] ?? null) : null}
                    onOpenOutletMatrix={() => setPduMatrixEq(eq)}
                  />
                ))}
                {isMountingMode && (
                  <button
                    type="button"
                    onClick={() => onSlotClick?.(0, 'Right')}
                    className="w-full py-2 rounded-xs border border-dashed border-indigo-500/50 hover:border-indigo-400 bg-indigo-950/20 hover:bg-indigo-950/40 text-center text-[8px] font-mono text-indigo-300 transition-all cursor-pointer flex flex-col items-center justify-center gap-0.5"
                    title="Добавить дополнительное 0U оборудование (Ввод B)"
                  >
                    <span className="text-xs font-bold leading-none">+</span>
                    <span>0U (B)</span>
                  </button>
                )}
              </>
            ) : (
              <ZeroUGhostPocket
                mount="Right"
                label="+ PDU B (0U)"
                isMountingMode={isMountingMode}
                onClick={() => onSlotClick?.(0, 'Right')}
              />
            )}
          </ZeroUDroppableColumn>
        </div>

        {/* Floating Tooltip */}
        {tooltipData && hoveredEqId === tooltipData.eq.id && (
          <DeviceTooltip
            eq={tooltipData.eq}
            clientX={tooltipData.clientX}
            clientY={tooltipData.clientY}
          />
        )}

        {/* PDU 24-Outlet Matrix Popover */}
        {pduMatrixEq && (
          <PduOutletMatrixPopover
            pdu={pduMatrixEq}
            allEquipment={equipmentList}
            onClose={() => setPduMatrixEq(null)}
          />
        )}
      </div>
    </DndContext>
  );
};
